import { HTTPError } from 'ky'

import { api } from './api.ts'

/** The three answers a builder may give when asked who should have access. */
export type Visibility = 'everyone' | 'owner' | 'allow-list'

export type PageAccess = {
  id: string
  path: string
  title: string
  owner: string
  visibility: Visibility
  allowedUsers: string[]
  source: 'declared' | 'live'
  updatedAt?: string | null
  updatedBy?: string | null
  canView: boolean
  canAdminister: boolean
}

export type Viewer = {
  email: string
  name: string
  isSuperUser: boolean
  source: 'id-token' | 'local-dev'
}

export type AccessView = {
  viewer: Viewer
  pages: PageAccess[]
  superUsers: string[]
  lockedSuperUsers: string[]
  platformPaths: string[]
}

export type PageAccessChange = {
  pageId: string
  visibility: Visibility
  allowedUsers?: string[]
  owner?: string
}

/** What each choice is called on screen. The API never sees these words. */
export const VISIBILITY_LABELS: Record<Visibility, string> = {
  everyone: 'All employees',
  owner: 'Just me',
  'allow-list': 'Specific people'
}

export const VISIBILITY_ORDER: Visibility[] = [
  'everyone',
  'owner',
  'allow-list'
]

/**
 * A refusal from the access API, carrying the status so a caller can tell
 * "sign in" from "you may not" from "this is not configured".
 */
export class AccessRequestError extends Error {
  readonly status: number
  readonly details: string[]

  constructor(status: number, message: string, details: string[] = []) {
    super(message)
    this.name = 'AccessRequestError'
    this.status = status
    this.details = details
  }
}

/**
 * What a refusal looks like on the wire.
 *
 * `message` is what the backend sends: blytz-utils owns the error body and puts
 * every reason the service gave into that one string. `error` and the two
 * arrays are the shape the service builds internally, kept here because a
 * direct answer from the service — a test double, or a future handler that does
 * not pass through the wrapper — still uses it.
 */
type ProblemBody = {
  message?: string
  error?: string
  errors?: string[]
  missing?: string[]
}

/** Turn any thrown request into an AccessRequestError that says something useful. */
export async function toAccessError(
  error: unknown
): Promise<AccessRequestError> {
  if (!(error instanceof HTTPError)) {
    return new AccessRequestError(0, 'the access service could not be reached')
  }

  let body: ProblemBody
  try {
    body = await error.response.json<ProblemBody>()
  } catch {
    body = {}
  }

  const details = body.errors ?? body.missing ?? []

  return new AccessRequestError(
    error.response.status,
    body.message ?? body.error ?? 'the access service refused the request',
    details
  )
}

async function request<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run()
  } catch (error) {
    throw await toAccessError(error)
  }
}

export function fetchAccess(): Promise<AccessView> {
  return request(() => api.get('pageAccess').json<AccessView>())
}

export function savePageAccess(
  change: PageAccessChange
): Promise<{ page: PageAccess }> {
  return request(() =>
    api.post('setPageAccess', { json: change }).json<{ page: PageAccess }>()
  )
}

export function saveSuperUsers(
  superUsers: string[]
): Promise<{ superUsers: string[]; lockedSuperUsers: string[] }> {
  return request(() =>
    api
      .post('setSuperUsers', { json: { superUsers } })
      .json<{ superUsers: string[]; lockedSuperUsers: string[] }>()
  )
}

/**
 * The sign-in screen, which stays reachable even when the access API refuses
 * to answer. A person who is signed out cannot be sent to the one screen that
 * fixes that by a guard which needs the same sign-in to decide.
 */
export const SIGN_IN_PATH = '/sign-in'

export function normalizePath(pathname: string): string {
  const path = pathname.split('?')[0]

  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path
}

/**
 * What the access rules say about one route.
 *
 * `platform` is a route the platform owns — the launcher, sign-in, and this
 * screen — which every employee who can sign in may open. `undefined` means no
 * page is registered at that path, which denies: a route that nobody declared
 * has no owner and no answer to who may see it.
 */
export function accessForPath(
  view: AccessView,
  pathname: string
): PageAccess | 'platform' | undefined {
  const path = normalizePath(pathname)
  if (view.platformPaths.includes(path)) {
    return 'platform'
  }

  return view.pages.find(page => normalizePath(page.path) === path)
}

/** The pages a viewer may open, for the navigation and the launcher. */
export function viewablePages(view: AccessView): PageAccess[] {
  return view.pages.filter(page => page.canView)
}

/** The pages a viewer may change the rules for. */
export function administrablePages(view: AccessView): PageAccess[] {
  return view.pages.filter(page => page.canAdminister)
}
